<template>
  <div
    v-if="loading && !rightToWork"
    class="tw-clear-both tw-py-6"
    data-test="right-to-work-loading"
  >
    <WdsSpinner indeterminate>
      <WdsIcon name="bam" :size="16" />
    </WdsSpinner>
  </div>

  <WdsAlert v-else-if="queryFailed" class="tw-mt-4" error data-test="right-to-work-query-error">
    {{ $t('Your right-to-work documents could not be loaded. Please reload the page.') }}
  </WdsAlert>

  <!-- Renders nothing when the nationality is exempt: where no market asks, nothing may be supplied. -->
  <div v-else-if="isRequired" class="tw-clear-both" data-test="right-to-work-documents">
    <WdsDivider class="tw-my-6" />

    <WdsHeading as="h3" type="title4" class="tw-mb-2">
      {{ $t('Right-to-work documents') }}
    </WdsHeading>

    <WdsText secondary class="tw-mb-4">
      {{ $t('Please upload your right-to-work documentation here.') }}
    </WdsText>

    <WdsText secondary class="tw-mb-4">
      {{
        $t(
          'To verify your eligibility to work, Worksome will collect your passport and, where applicable, your work or residence permit and share these documents with :client.',
          { client: requiredByLabel },
        )
      }}
      <a href="/pages/privacy" target="_blank" rel="noopener" class="tw-underline">
        {{ $t('For more information, see our Privacy Statement.') }}
      </a>
    </WdsText>

    <WdsAlert
      v-if="changesRequested"
      class="tw-mb-4"
      warning
      icon
      data-test="right-to-work-changes-requested"
    >
      {{
        $t(
          'Your client has reviewed your compliance and requested changes. Please upload your right-to-work documents again.',
        )
      }}
    </WdsAlert>

    <div
      v-for="requirement in requirements"
      :key="requirement.documentType"
      class="tw-mb-4 tw-p-4 tw-border tw-border-gray-200 tw-rounded-md tw-bg-white"
      :data-test="`right-to-work-${requirement.testId}`"
    >
      <WdsText weight="bold" class="tw-mb-2">
        {{ requirement.label }}
        <span aria-hidden="true">*</span>
        <span class="tw-sr-only">{{ $t('Required') }}</span>
      </WdsText>

      <WdsButton
        type="button"
        variant="secondary"
        :disabled="saving"
        :data-test="`right-to-work-choose-${requirement.testId}`"
        @click="chooseFileFor(requirement.documentType)"
      >
        {{ requirement.hasFile ? $t('Replace document') : $t('Choose file') }}
      </WdsButton>

      <div v-if="requirement.fileName" class="tw-flex tw-items-center tw-gap-1 tw-mt-2">
        <WdsText secondary size="small">
          {{ requirement.fileName }}
        </WdsText>
        <WdsButtonIcon
          v-if="requirement.viewUrl"
          transparent-background
          sm
          :aria-label="$t('Preview :document', { document: requirement.label })"
          :data-test="`right-to-work-open-${requirement.testId}`"
          @click="previewing = requirement"
        >
          <WdsIcon name="document" :size="16" />
        </WdsButtonIcon>
      </div>

      <WdsText
        v-if="requirement.expiresLabel"
        secondary
        size="small"
        :error="requirement.isExpired"
        class="tw-mt-1"
      >
        {{ requirement.expiresLabel }}
      </WdsText>

      <div
        v-if="requirement.requiresExpiry && requirement.pendingFile"
        class="tw-mt-4 tw-max-w-xs"
      >
        <WdsInputDate
          :model-value="requirement.pendingExpiry"
          format="yyyy-mm-dd"
          :title="`${$t('Expiration date')} *`"
          :min-date="earliestExpiryDate()"
          :data-test="`right-to-work-expiry-${requirement.testId}`"
          @update:model-value="setExpiry(requirement.documentType, $event)"
        />
      </div>
    </div>

    <!--
      The worker's account settings are a Bootstrap form, so the feedback and save
      control match the identification form above it there; the managed-worker
      profile is WDS throughout.
    -->
    <template v-if="bootstrapForm">
      <div v-if="errorMessage" class="alert alert-danger" role="alert" data-test="right-to-work-error">
        {{ errorMessage }}
      </div>
      <div v-if="saved" class="alert alert-success" role="alert" data-test="right-to-work-saved">
        {{ $t('Your right-to-work documents have been saved') }}
      </div>

      <div class="form-group">
        <div class="col-md-6 col-md-offset-4">
          <button
            type="button"
            class="btn btn-primary no-spinner"
            :disabled="saving || !canSave"
            data-test="right-to-work-save"
            @click="onSave"
          >
            {{ saving ? $t('Saving...') : $t('Save') }}
          </button>
        </div>
      </div>
    </template>

    <template v-else>
      <WdsAlert
        v-if="errorMessage"
        class="tw-mt-4"
        closable
        error
        data-test="right-to-work-error"
        @close="errorMessage = null"
      >
        {{ $t('An error occurred') }}
        <template #description>{{ errorMessage }}</template>
      </WdsAlert>

      <WdsAlert
        v-else-if="saved"
        class="tw-mt-4"
        closable
        success
        data-test="right-to-work-saved"
        @close="saved = false"
      >
        {{ $t('Your right-to-work documents have been saved') }}
      </WdsAlert>

      <div class="tw-mt-4">
        <WdsButton
          type="button"
          :disabled="saving || !canSave"
          primary
          data-test="right-to-work-save"
          @click="onSave"
        >
          <div class="tw-flex tw-items-center">
            <span>{{ $t('Save') }}</span>
            <WdsSpinner v-show="saving" class="tw-ml-2" indeterminate>
              <WdsIcon name="bam" :size="16" />
            </WdsSpinner>
          </div>
        </WdsButton>
      </div>
    </template>

    <RestrictedDocumentPreviewModal
      :show="previewing !== null"
      :view-url="previewing?.viewUrl ?? null"
      :title="previewing?.label ?? ''"
      @close="previewing = null"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, reactive, ref } from 'vue'
import axios from 'axios'
import { useQuery } from '@vue/apollo-composable'
import { useFileDialog } from '@vueuse/core'
import {
  WdsAlert,
  WdsButton,
  WdsButtonIcon,
  WdsDivider,
  WdsHeading,
  WdsIcon,
  WdsInputDate,
  WdsSpinner,
  WdsText,
} from '@worksome/wds-components'
import RestrictedDocumentPreviewModal from '@/components/Identification/RestrictedDocumentPreviewModal.vue'
import { useTranslation } from '@/composables/translation'
import { graphql } from '@/gql'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import { earliestExpiryDate, formatDocumentDate, isDocumentExpired } from '@/utils/restrictedDocument'

// Laid out like NlKvkCertificateForm; the server resolves requirements for the selected nationality.
const props = withDefaults(
  defineProps<{
    workerGlobalId: string
    /** Nationality selected in the parent form, possibly unsaved. */
    selectedNationality?: string | null
    /** Render the alerts and save button with the Bootstrap form classes of the surrounding legacy form. */
    bootstrapForm?: boolean
  }>(),
  { selectedNationality: null, bootstrapForm: false },
)

const { $t } = useTranslation()

const WorkerRightToWorkQuery = graphql(`
  query workerRightToWork($id: ID!, $nationality: String) {
    worker(id: $id) {
      id
      rightToWork(nationality: $nationality) {
        requirements {
          documentType
          label
          requiresExpiry
        }
        documents {
          id
          documentType
          filename
          expiresAt
          viewUrl
        }
        changesRequested
        requiredBy
      }
    }
  }
`)

const {
  result,
  loading,
  error: queryError,
  refetch,
} = useQuery(
  WorkerRightToWorkQuery,
  () => ({ id: props.workerGlobalId, nationality: props.selectedNationality || null }),
  { clientId: 'publicApi' },
)

const rightToWork = computed(() => result.value?.worker?.rightToWork ?? null)
const queryFailed = computed(() => Boolean(queryError.value) && rightToWork.value === null)
const isRequired = computed(() => (rightToWork.value?.requirements?.length ?? 0) > 0)
const changesRequested = computed(() => Boolean(rightToWork.value?.changesRequested))

// A worker always has at least one requiring hire, so the fallback is belt and braces.
const requiredByLabel = computed(() => {
  const names = rightToWork.value?.requiredBy ?? []

  return names.length > 0 ? names.join(', ') : $t('your client')
})

type Selection = { file: File; expiry: string }

const selections = reactive<Record<string, Selection>>({})

const saving = ref(false)
const saved = ref(false)
const errorMessage = ref<string | null>(null)

function expiresLabelFor(expiresAt: string | null | undefined, expired: boolean, pending: boolean): string | null {
  if (pending || !expiresAt) return null

  const date = formatDocumentDate(expiresAt)

  return expired ? $t('Expired :date', { date }) : $t('Expires :date', { date })
}

const requirements = computed(() =>
  (rightToWork.value?.requirements ?? []).map((requirement) => {
    const type = String(requirement.documentType)
    const document = (rightToWork.value?.documents ?? []).find(
      (candidate) => candidate.documentType === requirement.documentType,
    )
    const pending = selections[type] ?? null
    const expired = isDocumentExpired(document?.expiresAt)

    return {
      documentType: type,
      label: requirement.label,
      requiresExpiry: requirement.requiresExpiry,
      testId: type.toLowerCase().replace(/_/g, '-'),
      pendingFile: pending?.file ?? null,
      pendingExpiry: pending?.expiry ?? '',
      hasFile: Boolean(pending || document),
      fileName: pending?.file.name ?? document?.filename ?? null,
      viewUrl: pending ? null : document?.viewUrl ?? null,
      isExpired: expired,
      expiresLabel: expiresLabelFor(document?.expiresAt, expired, pending !== null),
    }
  }),
)

type Requirement = (typeof requirements.value)[number]

const previewing = ref<Requirement | null>(null)

const pendingRequirements = computed(() =>
  requirements.value.filter((requirement) => requirement.pendingFile !== null),
)

const canSave = computed(
  () =>
    pendingRequirements.value.length > 0 &&
    pendingRequirements.value.every(
      (requirement) => !requirement.requiresExpiry || requirement.pendingExpiry !== '',
    ),
)

const { open: openFileDialog, onChange: onFileChosen } = useFileDialog({
  accept: 'image/jpeg,image/png,application/pdf',
  multiple: false,
  reset: true,
})

// The dialog is one instance, so the requirement it was opened for has to be remembered.
let choosingFor: string | null = null

function chooseFileFor(documentType: string): void {
  choosingFor = documentType
  openFileDialog()
}

onFileChosen((files) => {
  const file = files?.[0]
  if (!file || choosingFor === null) return

  selections[choosingFor] = { file, expiry: selections[choosingFor]?.expiry ?? '' }
  errorMessage.value = null
  saved.value = false
})

function setExpiry(documentType: string, value: unknown): void {
  const selection = selections[documentType]
  if (!selection) return

  selection.expiry = typeof value === 'string' ? value : ''
}

function readableError(error: unknown): string {
  const { response } = error as {
    response?: { data?: { errors?: Record<string, string[]>; message?: string } }
  }

  if (response?.data?.errors) {
    return Object.values(response.data.errors).flat().join(' ')
  }

  return response?.data?.message ?? $t('The document could not be uploaded.')
}

// Uploads over the authenticated REST route: the federated GraphQL gateway cannot carry multipart uploads.
async function uploadRequirement(requirement: Requirement): Promise<void> {
  const form = new FormData()
  form.append('worker_id', props.workerGlobalId)
  form.append('document_type', requirement.documentType)
  form.append('file', requirement.pendingFile as File)
  if (requirement.pendingExpiry !== '') {
    form.append('expires_at', requirement.pendingExpiry)
  }
  if (props.selectedNationality) {
    form.append('nationality', props.selectedNationality)
  }

  await axios.post(laravelNamedRoute('files.restricted.store'), form)
}

async function onSave(): Promise<void> {
  if (!canSave.value) return

  saving.value = true
  errorMessage.value = null
  saved.value = false

  try {
    for (const requirement of pendingRequirements.value) {
      await uploadRequirement(requirement)
      delete selections[requirement.documentType]
    }

    saved.value = true
  } catch (error) {
    errorMessage.value = readableError(error)
  } finally {
    // Also on failure: an earlier upload in the loop may have landed, and the picker must show it.
    await refetch()
    saving.value = false
  }
}
</script>
